import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ShopContext = createContext(null);
const EMPTY = { items: [], expires_at: null };
const GUEST_KEY = "cs_guest_cart";
const readGuest = () => { try { return JSON.parse(localStorage.getItem(GUEST_KEY)) || []; } catch { return []; } };
const writeGuest = (items) => localStorage.setItem(GUEST_KEY, JSON.stringify(items.map((i) => ({ product_id: i.product_id, quantity: i.quantity }))));

export function ShopProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(EMPTY);
  const [cartOpen, setCartOpen] = useState(false);
  const [wishlist, setWishlist] = useState([]);

  const priceGuest = useCallback(async (items) => {
    if (!items.length) { writeGuest([]); setState(EMPTY); return EMPTY; }
    const { data } = await api.post("/cart/guest", { items });
    writeGuest(data.items);
    setState(data);
    return data;
  }, []);

  const loadCart = useCallback(async () => {
    if (user === false) return priceGuest(readGuest()).catch(() => setState(EMPTY));
    if (!user) return undefined;
    const pending = readGuest();
    if (pending.length && user.account_type !== "b2b") {
      localStorage.removeItem(GUEST_KEY);
      for (const it of pending) await api.post("/cart/items", it).catch(() => {});
    }
    return api.get("/cart").then((r) => {
      setState(r.data);
      if (r.data.expired_at) toast.info("Il tuo carrello è scaduto dopo 6 ore e i prodotti sono tornati disponibili per tutti.", { duration: 8000 });
    }).catch(() => setState(EMPTY));
  }, [user, priceGuest]);

  useEffect(() => { localStorage.removeItem("mr_cart"); loadCart(); }, [loadCart, user?.account_type]);

  useEffect(() => {
    if (user) api.get("/wishlist").then((r) => setWishlist(r.data.ids)).catch(() => {});
    else setWishlist([]);
  }, [user]);

  useEffect(() => {
    if (!state.expires_at) return undefined;
    const ms = new Date(state.expires_at) - Date.now();
    const t = setTimeout(loadCart, Math.max(1000, ms + 1500));
    return () => clearTimeout(t);
  }, [state.expires_at, loadCart]);

  const setGuestQty = async (pid, quantity) => {
    const items = readGuest().filter((i) => i.product_id !== pid);
    if (quantity > 0) items.push({ product_id: pid, quantity });
    try {
      const data = await priceGuest(items);
      const got = data.items.find((i) => i.product_id === pid)?.quantity || 0;
      if (data.notes?.length) toast.error(data.notes[0]);
      return quantity === 0 || got === quantity;
    } catch (err) { toast.error(formatApiError(err)); return false; }
  };

  const setQty = async (pid, quantity) => {
    if (!user) return setGuestQty(pid, quantity);
    try {
      const { data } = await api.post("/cart/items", { product_id: pid, quantity });
      setState(data);
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    }
  };

  const addToCart = async (p, qty = 1) => {
    const current = (user ? state.items : readGuest()).find((i) => i.product_id === p.id)?.quantity || 0;
    if (await setQty(p.id, current + qty)) toast.success("Prodotto aggiunto al carrello!", { description: user ? `${p.name} · riservato per te fino a 6 ore` : p.name });
  };
  const updateQty = (pid, qty) => setQty(pid, Math.max(1, qty));
  const removeFromCart = (pid) => setQty(pid, 0);
  const clearCart = () => { if (!user) writeGuest([]); setState(EMPTY); };
  const emptyCart = () => (user ? api.delete("/cart").then((r) => setState(r.data)) : Promise.resolve(clearCart()));

  const toggleWishlist = async (pid) => {
    if (!user) {
      toast.error("Accedi per salvare i preferiti");
      return;
    }
    const had = wishlist.includes(pid);
    const { data } = await api.post(`/wishlist/${pid}`);
    setWishlist(data.ids);
    toast.success(had ? "Rimosso dalla lista desideri" : "Aggiunto alla lista desideri");
  };

  const cart = state.items;
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cart.reduce((s, i) => s + i.quantity * i.price, 0);

  return (
    <ShopContext.Provider
      value={{ cart, cartExpiresAt: state.expires_at, reloadCart: loadCart, cartOpen, setCartOpen, addToCart, updateQty, removeFromCart, clearCart, emptyCart, cartCount, cartTotal, wishlist, toggleWishlist }}
    >
      {children}
    </ShopContext.Provider>
  );
}

export const useShop = () => useContext(ShopContext);
